import { ANIMATION_FRAMES, CHAR_FEET_X, CHAR_FEET_Y, CHAR_FRAME_H, CHAR_FRAME_W } from '@enugu/shared';
import { shade } from './paint';
/**
 * Procedural paper-doll characters: 64x128 frames, feet at (32, 120)
 * (vision §6). Every layer is drawn from the same per-frame pose, which is
 * what keeps layers in sync; real art gets the same guarantee from the
 * shared frame grid (docs/61).
 *
 * Clothing layers are drawn in light neutral greys so a sprite tint can
 * colour them; skin tones are separate pre-rendered body variants (never a
 * tint), as the vision requires.
 */
export const SKIN_TONES = {
    skin1: 0x3f2516,
    skin2: 0x5e3720,
    skin3: 0x7f4f2f,
};
export const VARIANTS = {
    body: Object.keys(SKIN_TONES),
    shoes: ['slippers'],
    bottom: ['trousers', 'wrapper'],
    top: ['tee', 'ankara', 'isiagu'],
    hair: ['lowcut', 'afro', 'braids'],
    headwear: ['gele'],
};
/** Which variants accept a tint (neutral base colour). */
export const TINTABLE = new Set(['bottom/trousers', 'bottom/wrapper', 'top/tee', 'headwear/gele']);
/** Clothing tints: bright Enugu street colours. */
export const CLOTH_TINTS = [0xd94f3d, 0x2e6fb7, 0xf2c14e, 0x3f9b5b, 0xf5f1e8, 0x7a4ea3, 0x1f2a44, 0xe58a3a, 0x6b4f3a, 0xbfbfbf, 0x2aa198, 0xc2185b];
const AUTHORED_ANIMS = ['idle', 'walk'];
const DIRS = ['SE', 'NE'];
const HAIR = 0x15110e;
/** Everything a layer draws stays inside this region of the 64x128 frame (atlas trim). */
const TRIM = { x: 8, y: 20, w: 48, h: 107 };
function pose(anim, f, dir) {
    let bob = 0;
    let swing = 0;
    if (anim === 'walk') {
        swing = Math.sin((f / ANIMATION_FRAMES.walk) * Math.PI * 2);
        bob = -1.6 * (1 - Math.abs(swing));
    }
    else {
        bob = [0, -0.5, -1, -0.5][f % 4];
    }
    // Screen direction of "forward": SE = down-right, NE = up-right.
    const fx = 4.2 * swing;
    const fy = (dir === 'SE' ? 1.6 : -1.6) * swing;
    return {
        bob,
        lfx: CHAR_FEET_X - 3.6 + fx,
        lfy: CHAR_FEET_Y - 1 + fy,
        rfx: CHAR_FEET_X + 3.6 - fx,
        rfy: CHAR_FEET_Y - 1 - fy,
        // Arms swing against the legs.
        lhx: 20.5 - fx * 0.7,
        lhy: 89 + bob - fy * 0.7,
        rhx: 44.5 + fx * 0.7,
        rhy: 89 + bob + fy * 0.7,
        dir,
    };
}
const HIP_Y = 92;
const SHOULDER_Y = 65.5;
function limb(g, x0, y0, x1, y1, width, color) {
    g.moveTo(x0, y0).lineTo(x1, y1).stroke({ width, color, cap: 'round' });
}
function drawBody(g, p, skin) {
    const b = p.bob;
    const dark = shade(skin, 0.82);
    limb(g, 28.5, HIP_Y + b, p.lfx, p.lfy - 2, 6.2, p.dir === 'SE' ? dark : skin);
    limb(g, 35.5, HIP_Y + b, p.rfx, p.rfy - 2, 6.2, p.dir === 'SE' ? skin : dark);
    g.ellipse(p.lfx, p.lfy, 3.2, 1.8).fill(dark);
    g.ellipse(p.rfx, p.rfy, 3.2, 1.8).fill(dark);
    g.roundRect(22, 61 + b, 20, 33, 6).fill(skin);
    limb(g, 22.5, SHOULDER_Y + b, p.lhx, p.lhy, 5, skin);
    limb(g, 41.5, SHOULDER_Y + b, p.rhx, p.rhy, 5, dark);
    g.circle(p.lhx, p.lhy + 1, 2.6).fill(dark);
    g.circle(p.rhx, p.rhy + 1, 2.6).fill(dark);
    g.rect(29.5, 55 + b, 6, 8).fill(dark);
    g.ellipse(24.6, 50 + b, 1.6, 2.4).fill(dark);
    g.ellipse(41.4, 50 + b, 1.6, 2.4).fill(dark);
    g.ellipse(33, 48.5 + b, 8.6, 9.6).fill(skin);
    if (p.dir === 'SE') {
        // Face turned slightly to screen-right.
        for (const ex of [30.4, 36.6]) {
            g.ellipse(ex, 48.8 + b, 1.7, 1.15).fill(0xf3ece2);
            g.circle(ex + 0.45, 48.9 + b, 0.85).fill(0x1a120c);
        }
        g.moveTo(29, 46.2 + b).lineTo(31.8, 45.8 + b).moveTo(35.2, 45.8 + b).lineTo(38, 46.2 + b).stroke({ width: 0.8, color: 0x1a120c });
        g.ellipse(34, 51.8 + b, 1.4, 1).fill(shade(skin, 0.7));
        g.moveTo(31.6, 54.4 + b).quadraticCurveTo(34, 55.6 + b, 36.4, 54.4 + b).stroke({ width: 0.9, color: 0x5a2a20 });
    }
}
function drawShoes(g, p) {
    for (const [x, y] of [[p.lfx, p.lfy], [p.rfx, p.rfy]]) {
        g.ellipse(x, y + 0.4, 3.9, 2).fill(0x2a2422);
        g.moveTo(x - 2.2, y - 0.8).lineTo(x + 2.2, y - 0.8).stroke({ width: 1, color: 0x8b2b22 });
    }
}
function drawBottom(g, p, variant) {
    const b = p.bob;
    const base = 0xececec;
    const dark = 0xbdbdbd;
    if (variant === 'trousers') {
        limb(g, 28.5, HIP_Y + b - 1, p.lfx, p.lfy - 4.5, 8, p.dir === 'SE' ? dark : base);
        limb(g, 35.5, HIP_Y + b - 1, p.rfx, p.rfy - 4.5, 8, p.dir === 'SE' ? base : dark);
        g.roundRect(22, 86 + b, 20, 9, 3).fill(base);
        g.rect(22, 86 + b, 20, 1.6).fill(dark);
        return;
    }
    // Wrapper (long skirt): hem follows the stride a little.
    const sway = (p.lfx - (CHAR_FEET_X - 3.6)) * 0.35;
    const hemY = 114.5;
    g.poly([22, 85 + b, 42, 85 + b, 44.5 + sway, hemY, 19.5 + sway, hemY], true).fill(base);
    for (const t of [0.3, 0.55, 0.8]) {
        const y = 85 + b + (hemY - 85 - b) * t;
        const xl = 22 - 2.5 * t + sway * t;
        g.rect(xl, y, 20 + 5 * t, 1.8).fill(dark);
    }
    g.rect(22, 85 + b, 20, 2).fill(dark);
}
function drawTop(g, p, variant) {
    const b = p.bob;
    const long = variant !== 'tee';
    const torsoH = long ? 32 : 28;
    const sleeveT = variant === 'isiagu' ? 0.85 : variant === 'ankara' ? 0.6 : 0.4;
    const base = variant === 'tee' ? 0xf2f2f2 : variant === 'ankara' ? 0xd9741c : 0x1e1b19;
    const elbow = (sx, hx, hy) => [sx + (hx - sx) * sleeveT, SHOULDER_Y + b + (hy - SHOULDER_Y - b) * sleeveT];
    const [lex, ley] = elbow(22.5, p.lhx, p.lhy);
    const [rex, rey] = elbow(41.5, p.rhx, p.rhy);
    limb(g, 22.5, SHOULDER_Y + b, lex, ley, 7, base);
    limb(g, 41.5, SHOULDER_Y + b, rex, rey, 7, shade(base, 0.85));
    g.roundRect(20.8, 60.5 + b, 22.4, torsoH, 6).fill(base);
    g.rect(32, 61 + b, 11.2, torsoH - 1).fill({ color: 0x000000, alpha: 0.08 }); // side shading
    if (p.dir === 'SE')
        g.ellipse(32.5, 61.5 + b, 4.2, 2).fill(shade(base, 0.75)); // collar
    if (variant === 'ankara') {
        for (let i = 0; i < 6; i++) {
            const x = 24 + (i % 3) * 7.5;
            const y = 66 + b + Math.floor(i / 3) * 11;
            g.circle(x, y, 2.6).fill(0x1f4e9c);
            g.circle(x, y, 1.1).fill(0xf2d03b);
        }
        g.rect(21, 84 + b, 22, 2).fill(0x1f4e9c);
    }
    else if (variant === 'isiagu') {
        for (let i = 0; i < 9; i++) {
            const x = 24.5 + (i % 3) * 7.5;
            const y = 66 + b + Math.floor(i / 3) * 8.5;
            g.circle(x, y, 1.6).fill(0xd4a017);
            g.circle(x, y, 0.6).fill(0xb3261e);
        }
    }
}
/** Crescent hair cap: outer arc over the top, back along the hairline. */
function cap(g, cx, cy, r, hairlineY, hairlineR, color, a0 = 165, a1 = 375) {
    const pts = [];
    for (let a = a0; a <= a1; a += 10) {
        const t = (a * Math.PI) / 180;
        pts.push(cx + Math.cos(t) * r, cy + Math.sin(t) * r);
    }
    for (let a = a1; a >= a0; a -= 10) {
        const t = (a * Math.PI) / 180;
        pts.push(cx + Math.cos(t) * hairlineR, hairlineY + Math.sin(t) * hairlineR * 0.55);
    }
    g.poly(pts, true).fill(color);
}
function drawHair(g, p, variant) {
    const b = p.bob;
    if (p.dir === 'NE') {
        // Back view: hair covers the head.
        if (variant === 'afro')
            g.circle(33, 45 + b, 12.4).fill(HAIR);
        else
            g.ellipse(33, 47.8 + b, 9.3, 9.9).fill(HAIR);
        if (variant === 'braids')
            for (let i = 0; i < 7; i++)
                limb(g, 26 + i * 2.3, 48 + b, 26 + i * 2.3 + (i - 3) * 0.4, 68 + b, 1.8, 0x241b15);
        return;
    }
    if (variant === 'afro') {
        cap(g, 33, 45 + b, 12.4, 45.5 + b, 9, HAIR);
    }
    else {
        cap(g, 33, 48 + b, 9.8, 46.4 + b, 8.4, HAIR, 170, 370);
        if (variant === 'braids') {
            for (const [x0, x1] of [[24.6, 23.6], [26.2, 25.6], [41.4, 42.4], [39.8, 40.4]])
                limb(g, x0, 47 + b, x1, 66 + b, 2, 0x241b15);
        }
    }
}
function drawHeadwear(g, p) {
    const b = p.bob;
    const base = 0xf4f4f4;
    const pleat = 0xc7c7c7;
    if (p.dir === 'SE') {
        g.poly([23.5, 46 + b, 42.5, 46 + b, 46, 35 + b, 40, 27 + b, 33, 31.5 + b, 26, 26.5 + b, 20, 34 + b], true).fill(base);
    }
    else {
        g.poly([23, 53 + b, 43, 53 + b, 46, 36 + b, 40, 27 + b, 33, 31.5 + b, 26, 26.5 + b, 20, 35 + b], true).fill(base);
    }
    for (const [x0, y0, x1, y1] of [[26, 27, 29, 44], [33, 32, 33, 45], [40, 28, 37, 44]]) {
        g.moveTo(x0, y0 + b).lineTo(x1, y1 + b).stroke({ width: 1, color: pleat });
    }
}
function frameItem(key, draw) {
    return { key, w: CHAR_FRAME_W, h: CHAR_FRAME_H, anchorX: CHAR_FEET_X, anchorY: CHAR_FEET_Y, trim: TRIM, draw };
}
/** All character frames for the "characters" procedural generator. */
export function characterArtItems() {
    const items = [];
    items.push({
        key: 'char/shadow/default',
        w: CHAR_FRAME_W,
        h: CHAR_FRAME_H,
        anchorX: CHAR_FEET_X,
        anchorY: CHAR_FEET_Y,
        trim: { x: 16, y: 113, w: 32, h: 14 },
        draw: (g) => g.ellipse(CHAR_FEET_X, CHAR_FEET_Y, 13, 4.5).fill({ color: 0x000000, alpha: 0.3 }),
    });
    for (const anim of AUTHORED_ANIMS) {
        for (const dir of DIRS) {
            for (let f = 0; f < ANIMATION_FRAMES[anim]; f++) {
                const p = pose(anim, f, dir);
                const k = (layer, variant) => `char/${layer}/${variant}/${anim}/${dir}/${f}`;
                for (const [variant, skin] of Object.entries(SKIN_TONES))
                    items.push(frameItem(k('body', variant), (g) => drawBody(g, p, skin)));
                items.push(frameItem(k('shoes', 'slippers'), (g) => drawShoes(g, p)));
                for (const v of VARIANTS.bottom)
                    items.push(frameItem(k('bottom', v), (g) => drawBottom(g, p, v)));
                for (const v of VARIANTS.top)
                    items.push(frameItem(k('top', v), (g) => drawTop(g, p, v)));
                for (const v of VARIANTS.hair)
                    items.push(frameItem(k('hair', v), (g) => drawHair(g, p, v)));
                items.push(frameItem(k('headwear', 'gele'), (g) => drawHeadwear(g, p)));
            }
        }
    }
    return items;
}
/**
 * DollFrameProvider over AssetManager textures. Strips are resolved once per
 * (layer, variant, anim, dir) and cached. Animations without art fall back
 * to idle, so the full VS1 animation set can be requested before it exists.
 */
export class ProceduralDollFrames {
    assets;
    cache = new Map();
    constructor(assets) {
        this.assets = assets;
    }
    strip(layer, variant, anim, dir) {
        const key = `${layer}/${variant}/${anim}/${dir}`;
        let s = this.cache.get(key);
        if (s)
            return s;
        s = [];
        if (layer === 'shadow') {
            const t = this.assets.tryTexture('char/shadow/default');
            if (t)
                s.push(t);
        }
        else {
            for (let f = 0; f < ANIMATION_FRAMES[anim]; f++) {
                const t = this.assets.tryTexture(`char/${key}/${f}`);
                if (t)
                    s.push(t);
            }
            if (s.length === 0 && anim !== 'idle')
                return this.strip(layer, variant, 'idle', dir);
        }
        this.cache.set(key, s);
        return s;
    }
}
/** Random outfit from the procedural wardrobe. */
export function randomOutfit(r) {
    const pick = (a) => a[Math.floor(r() * a.length)];
    const tint = () => pick(CLOTH_TINTS);
    const top = pick(VARIANTS.top);
    const bottom = r() < 0.65 ? 'trousers' : 'wrapper';
    const gele = bottom === 'wrapper' && r() < 0.45;
    const layers = {
        shadow: 'default',
        body: pick(VARIANTS.body),
        shoes: 'slippers',
        bottom,
        top,
        hair: pick(VARIANTS.hair),
    };
    const tints = { bottom: tint() };
    if (TINTABLE.has(`top/${top}`))
        tints.top = tint();
    if (gele) {
        layers.headwear = 'gele';
        tints.headwear = pick([0xd4a017, 0xc2185b, 0x2e6fb7, 0x3f9b5b, 0xe58a3a]);
    }
    return { layers, tints };
}
